import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import EmailIcon from "@mui/icons-material/Email";
import TimerIcon from "@mui/icons-material/Timer";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * ContactDialog — Dialog con información de contacto del paciente y temporizador de llamada.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   patient: { name, cipId, phone, mobile, email } | null,
 * }} props
 */
export default function ContactDialog({ open, onClose, patient }) {
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setTimerRunning(false);
      setElapsed(0);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [open]);

  // Timer tick
  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerRunning]);

  const handleStart = () => setTimerRunning(true);
  const handleStop = () => setTimerRunning(false);
  const handleReset = () => {
    setTimerRunning(false);
    setElapsed(0);
  };

  const handleCopy = useCallback((text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  }, []);

  if (!patient) return null;

  const hasPhone = !!patient.phone;
  const hasMobile = !!patient.mobile;
  const hasEmail = !!patient.email;

  // Timer color based on duration
  let timerColor = "#16A34A"; // green < 5min
  if (elapsed >= 600) timerColor = "#DC2626"; // red >= 10min
  else if (elapsed >= 300) timerColor = "#D97706"; // amber >= 5min

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PersonIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {patient.name}
              </Typography>
              {patient.cipId && (
                <Typography variant="caption" color="text.secondary">
                  CIP: {patient.cipId}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {/* Contact Information */}
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
          Contact Information
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {/* Phone */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PhoneIcon sx={{ fontSize: 20, color: hasPhone ? "primary.main" : "text.disabled" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Phone</Typography>
              <Typography variant="body2" fontWeight={500}>
                {patient.phone || "Not available"}
              </Typography>
            </Box>
            {hasPhone && (
              <IconButton size="small" onClick={() => handleCopy(patient.phone)} title="Copy">
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Stack>

          {/* Mobile */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SmartphoneIcon sx={{ fontSize: 20, color: hasMobile ? "info.main" : "text.disabled" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Mobile</Typography>
              <Typography variant="body2" fontWeight={500}>
                {patient.mobile || "Not available"}
              </Typography>
            </Box>
            {hasMobile && (
              <IconButton size="small" onClick={() => handleCopy(patient.mobile)} title="Copy">
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Stack>

          {/* Email */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <EmailIcon sx={{ fontSize: 20, color: hasEmail ? "secondary.main" : "text.disabled" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body2" fontWeight={500}>
                {patient.email || "Not available"}
              </Typography>
            </Box>
            {hasEmail && (
              <IconButton size="small" onClick={() => handleCopy(patient.email)} title="Copy">
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* Call Timer */}
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
          Call Timer
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            p: 3,
            bgcolor: timerRunning ? `${timerColor}08` : "#F8FAFC",
            borderRadius: 2,
            border: 1,
            borderColor: timerRunning ? timerColor : "divider",
            transition: "all 0.3s ease",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <TimerIcon sx={{ color: timerColor, fontSize: 28 }} />
            <Typography
              variant="h3"
              fontWeight={700}
              fontFamily="monospace"
              sx={{ color: timerColor, letterSpacing: 2 }}
            >
              {formatTime(elapsed)}
            </Typography>
          </Stack>

          {timerRunning && (
            <Chip
              label="Call in progress..."
              size="small"
              sx={{
                bgcolor: `${timerColor}18`,
                color: timerColor,
                fontWeight: 600,
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.6 },
                  "100%": { opacity: 1 },
                },
              }}
            />
          )}

          <Stack direction="row" spacing={1.5}>
            {!timerRunning ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayArrowIcon />}
                onClick={handleStart}
                size="large"
                sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
              >
                Start Call
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStop}
                size="large"
                sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
              >
                Stop
              </Button>
            )}
            {elapsed > 0 && (
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                size="large"
                sx={{ textTransform: "none" }}
              >
                Reset
              </Button>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
